import * as React from 'react'

/**
 * Beta-phase notice, rendered by the app shell so it sits above every pilet page.
 *
 * The "switch" action asks the content API to move this visitor off the beta UI.
 * Same-origin relative path on purpose — like `whoami`, the request must carry the
 * `__Host-` session cookie, which the dev server's proxy only preserves on-origin.
 *
 * The API does not answer with an HTTP 3xx; it returns a JSON payload carrying the
 * URL to send the browser to. We navigate there with a full document load (not a
 * client-side route change) so the newly selected UI is fetched fresh.
 */
const SWITCH_URL = '/api/3.0/ui/switch/default'

interface SwitchResponse {
  /** Absolute or relative URL the browser should load after switching. */
  redirect?: string;
}

const BetaBanner: React.FC = () => {
  const [busy, setBusy] = React.useState(false)

  const handleSwitch = React.useCallback(
    async (event: React.MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault()
      if (busy) return
      setBusy(true)
      try {
        const res = await fetch(SWITCH_URL)
        if (!res.ok) throw new Error(`switch failed: ${res.status}`)
        const data = (await res.json()) as SwitchResponse
        // A full navigation both switches and reloads; fall back to reloading the
        // current URL if the payload carried no redirect.
        window.location.assign(data.redirect ?? window.location.href)
      } catch {
        // Leave the beta UI in place on failure — the visitor can retry.
        setBusy(false)
      }
    },
    [busy]
  )

  return (
    <div className='BetaBanner' role='region' aria-label='Version bêta'>
      <p className='BetaBanner__text'>
        Cette interface est en phase <strong>bêta</strong>.{' '}
        <a
          href={SWITCH_URL}
          className='BetaBanner__link'
          onClick={handleSwitch}
          aria-busy={busy}
        >
          Revenir à la version standard
        </a>
      </p>
    </div>
  )
}

export default BetaBanner
